import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnterprise, EnterpriseProvider } from '../../contexts/EnterpriseContext';
import { Badge } from '../../components/ui/badge';
import { Toaster, toast } from 'sonner';
import {
  Zap, ChevronDown, ChevronUp, CheckCircle2, Search, Sparkles, ArrowLeft, ArrowRight, FileText
} from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_CODER_QUEUE = [
  { id: 'CHT-101', patient: 'Robert Miller', provider: 'Dr. Anil Mehta', dos: '03/28/2026', type: 'Office Visit', status: 'awaiting' },
  { id: 'CHT-102', patient: 'Sandra Lee', provider: 'Dr. Sarah Park', dos: '03/28/2026', type: 'Follow-up', status: 'awaiting' },
  { id: 'CHT-103', patient: 'William Garcia', provider: 'Dr. James Wilson', dos: '03/27/2026', type: 'New Patient', status: 'review' },
  { id: 'CHT-104', patient: 'Patricia Davis', provider: 'Dr. Anil Mehta', dos: '03/27/2026', type: 'Office Visit', status: 'coded' },
  { id: 'CHT-105', patient: 'Michael Brown', provider: 'Dr. Sarah Park', dos: '03/27/2026', type: 'Procedure', status: 'coded' },
];

const AI_DX_SUGGESTIONS = [
  { code: 'E11.65', desc: 'Type 2 diabetes mellitus with hyperglycemia', confidence: 97, accepted: false },
  { code: 'I10', desc: 'Essential (primary) hypertension', confidence: 99, accepted: false },
  { code: 'E78.5', desc: 'Hyperlipidemia, unspecified', confidence: 92, accepted: false },
];

const AI_CPT_SUGGESTIONS = [
  { code: '99214', desc: 'Office visit, established patient, moderate complexity', confidence: 94, mods: ['25', '', '', ''], accepted: false },
  { code: '36415', desc: 'Routine venipuncture', confidence: 99, mods: ['', '', '', ''], accepted: false },
  { code: '80053', desc: 'Comprehensive metabolic panel', confidence: 96, mods: ['', '', '', ''], accepted: false },
];

const statusColors = { awaiting: '#9ca3af', review: '#d97706', coded: '#22c55e' };
const statusLabels = { awaiting: 'Awaiting', review: 'In review', coded: 'Coded' };

const CoderView = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const enterprise = useEnterprise();
  const currentRole = enterprise?.currentRole || 'coder';
  const [activeChart, setActiveChart] = useState(DEMO_CODER_QUEUE[0]?.id);
  const [queueFilter, setQueueFilter] = useState('tocode');
  const [dxSuggestions, setDxSuggestions] = useState(AI_DX_SUGGESTIONS);
  const [cptSuggestions, setCptSuggestions] = useState(AI_CPT_SUGGESTIONS);
  const [coderNote, setCoderNote] = useState('');
  const [overrideField, setOverrideField] = useState(null);
  const [codedToday, setCodedToday] = useState(2);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);

  const filtered = DEMO_CODER_QUEUE.filter(c => {
    if (queueFilter === 'tocode') return c.status === 'awaiting';
    if (queueFilter === 'review') return c.status === 'review';
    return c.status === 'coded';
  });

  const activeItem = DEMO_CODER_QUEUE.find(c => c.id === activeChart) || DEMO_CODER_QUEUE[0];

  const acceptDx = (i) => setDxSuggestions(prev => prev.map((d, idx) => idx === i ? { ...d, accepted: true } : d));
  const acceptCpt = (i) => setCptSuggestions(prev => prev.map((c, idx) => idx === i ? { ...c, accepted: true } : c));

  const handleMarkCoded = () => {
    setCodedToday(prev => prev + 1);
    toast.success(`Chart coded and sent to billing`);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', background: '#fff' }}
      data-testid="coder-view">
      {/* Top bar */}
      <div style={{ height: 48, background: '#111', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0 }}>
        <button onClick={() => navigate('/revq')} style={{ color: '#fff', opacity: 0.5, fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ChevronDown style={{ width: 10, height: 10, transform: 'rotate(90deg)' }} /> revQ
        </button>
        <Badge variant="outline" style={{ fontSize: 10, border: '1px solid rgba(107,92,231,0.4)', color: 'rgba(107,92,231,0.8)' }}>
          <Zap style={{ width: 10, height: 10 }} /> Billing Focus
        </Badge>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.5)', position: 'relative' }}>
          <button onClick={() => setShowRoleSwitcher(prev => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,.55)', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
            data-testid="coder-role-switcher">
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: enterprise?.roles?.[currentRole]?.color || '#3b82f6' }} />
            <span>{enterprise?.roles?.[currentRole]?.label || 'Coder'}</span>
            <ChevronDown style={{ width: 10, height: 10, opacity: 0.5 }} />
          </button>
          {showRoleSwitcher && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => setShowRoleSwitcher(false)} />
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 220, background: '#1a1a1a', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.4)', zIndex: 61, overflow: 'hidden' }}
                data-testid="coder-role-dropdown">
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,.3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Switch role view</span>
                </div>
                {Object.entries(enterprise?.roles || {}).map(([key, r]) => (
                  <button key={key}
                    onClick={() => { setShowRoleSwitcher(false); enterprise?.setRole(key);
                      if (key === 'supervisor') navigate('/revq/supervisor');
                      else if (key === 'coder') navigate('/revq/coder');
                      else if (key === 'denials') navigate('/revq/denials-focus');
                      else navigate('/revq/billing-focus');
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', border: 'none', cursor: 'pointer', textAlign: 'left', background: currentRole === key ? 'rgba(255,255,255,.08)' : 'transparent' }}
                    data-testid={`coder-switch-${key}`}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 12, fontWeight: currentRole === key ? 500 : 400, color: currentRole === key ? '#fff' : 'rgba(255,255,255,.6)', margin: 0 }}>{r.label}</p>
                    </div>
                    {currentRole === key && <CheckCircle2 style={{ width: 11, height: 11, color: '#22c55e', flexShrink: 0 }} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/revq')} style={{ color: 'rgba(255,255,255,.5)', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer' }}>
            &larr; Return
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar Queue */}
        <div style={{ width: 200, borderRight: '0.5px solid #e8e8e8', display: 'flex', flexDirection: 'column', background: '#fafafa', flexShrink: 0 }}>
          <div style={{ padding: '9px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
            <div className="flex items-center gap-1.5" style={{ background: '#f0f0f0', borderRadius: 5, padding: '3px 8px', width: 'fit-content' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />
              <span style={{ fontSize: 11, color: '#555' }}>Billing Focus mode &middot; Coder</span>
            </div>
          </div>

          {/* Queue tabs */}
          <div className="flex" style={{ borderBottom: '0.5px solid #f0f0f0' }}>
            {[
              { key: 'tocode', label: `To code (${DEMO_CODER_QUEUE.filter(c => c.status === 'awaiting').length})` },
              { key: 'review', label: `Review (${DEMO_CODER_QUEUE.filter(c => c.status === 'review').length})` },
              { key: 'coded', label: `Done (${DEMO_CODER_QUEUE.filter(c => c.status === 'coded').length})` },
            ].map(t => (
              <button key={t.key} onClick={() => setQueueFilter(t.key)}
                style={{ flex: 1, fontSize: 10, padding: '7px 4px', color: queueFilter === t.key ? '#111' : '#aaa', fontWeight: queueFilter === t.key ? 500 : 400, borderBottom: `2px solid ${queueFilter === t.key ? '#3b82f6' : 'transparent'}`, background: 'none', border: 'none', borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: queueFilter === t.key ? '#3b82f6' : 'transparent', cursor: 'pointer' }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Chart list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
            {filtered.map(c => (
              <button key={c.id} onClick={() => setActiveChart(c.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 6, marginBottom: 2,
                  background: activeChart === c.id ? '#fff' : 'transparent', border: activeChart === c.id ? '0.5px solid #e0e0e0' : '0.5px solid transparent',
                  cursor: 'pointer',
                }}
                data-testid={`coder-chart-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{c.patient}</span>
                  <span style={{ fontSize: 9, fontWeight: 500, color: statusColors[c.status], textTransform: 'uppercase' }}>{statusLabels[c.status]}</span>
                </div>
                <p style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{c.provider} &middot; {c.dos}</p>
                <p style={{ fontSize: 10, color: '#bbb' }}>{c.type}</p>
              </button>
            ))}
          </div>

          {/* Footer stats */}
          <div style={{ padding: '10px 14px', borderTop: '0.5px solid #f0f0f0', fontSize: 10, color: '#aaa' }}>
            <p>Charts coded today: {codedToday}</p>
            <p style={{ marginTop: 2 }}>Avg. coding time: 3.8 min</p>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {/* Claim header */}
          <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
            <div className="flex items-center gap-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#555' }}>
                {activeItem.patient.split(' ').map(w => w[0]).join('')}
              </div>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111', margin: 0 }}>{activeItem.patient}</h2>
                <p style={{ fontSize: 12, color: '#888', marginTop: 1 }}>{activeItem.provider} &middot; {activeItem.dos} &middot; {activeItem.type}</p>
              </div>
            </div>
          </div>

          {/* AI Code Suggestions */}
          <div style={{ background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: 12, padding: 16, marginBottom: 16 }}
            data-testid="coding-panel">
            <div className="flex items-center gap-2" style={{ marginBottom: 14 }}>
              <CheckCircle2 style={{ width: 14, height: 14, color: '#22c55e' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>AI code suggestions</span>
              <span style={{ fontSize: 11, color: '#888' }}>Review and confirm or override</span>
            </div>

            {/* Diagnosis Codes */}
            <p style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Diagnosis codes</p>
            <div className="space-y-2" style={{ marginBottom: 16 }}>
              {dxSuggestions.map((dx, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                  background: dx.accepted ? '#f0fdf4' : '#fff', border: `0.5px solid ${dx.accepted ? '#bbf7d0' : '#e0e0e0'}`,
                }}
                  data-testid={`dx-suggestion-${dx.code}`}>
                  <code style={{ fontSize: 12, fontWeight: 600, color: '#111', fontFamily: 'monospace', width: 60, flexShrink: 0 }}>{dx.code}</code>
                  <span style={{ fontSize: 12, color: '#555', flex: 1 }}>{dx.desc}</span>
                  <span style={{ fontSize: 10, color: '#888', flexShrink: 0 }}>{dx.confidence}%</span>
                  {dx.accepted ? (
                    <CheckCircle2 style={{ width: 14, height: 14, color: '#22c55e', flexShrink: 0 }} />
                  ) : (
                    <div className="flex gap-1">
                      <button onClick={() => acceptDx(i)}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: '#22c55e', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                        data-testid={`accept-dx-${dx.code}`}>Accept</button>
                      <button onClick={() => setOverrideField(`dx-${i}`)}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: '#fff', color: '#555', border: '0.5px solid #e0e0e0', cursor: 'pointer' }}
                        data-testid={`override-dx-${dx.code}`}>Override</button>
                    </div>
                  )}
                </div>
              ))}
              <button style={{ fontSize: 11, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
                onClick={() => toast.info('Add diagnosis search coming soon')}>
                + Add diagnosis
              </button>
            </div>

            {/* Procedure Codes */}
            <p style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Procedure codes</p>
            <div className="space-y-2" style={{ marginBottom: 16 }}>
              {cptSuggestions.map((cpt, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                  background: cpt.accepted ? '#f0fdf4' : '#fff', border: `0.5px solid ${cpt.accepted ? '#bbf7d0' : '#e0e0e0'}`,
                }}
                  data-testid={`cpt-suggestion-${cpt.code}`}>
                  <code style={{ fontSize: 12, fontWeight: 600, color: '#111', fontFamily: 'monospace', width: 60, flexShrink: 0 }}>{cpt.code}</code>
                  <span style={{ fontSize: 12, color: '#555', flex: 1 }}>{cpt.desc}</span>
                  <div className="flex gap-1" style={{ flexShrink: 0 }}>
                    {cpt.mods.map((m, mi) => (
                      <input key={mi} value={m} readOnly style={{ width: 28, height: 22, textAlign: 'center', fontSize: 10, border: '0.5px solid #e0e0e0', borderRadius: 4, background: m ? '#f0fdf4' : '#f9f9f9', color: '#555' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 10, color: '#888', flexShrink: 0 }}>{cpt.confidence}%</span>
                  {cpt.accepted ? (
                    <CheckCircle2 style={{ width: 14, height: 14, color: '#22c55e', flexShrink: 0 }} />
                  ) : (
                    <div className="flex gap-1">
                      <button onClick={() => acceptCpt(i)}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: '#22c55e', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                        data-testid={`accept-cpt-${cpt.code}`}>Accept</button>
                      <button onClick={() => setOverrideField(`cpt-${i}`)}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: '#fff', color: '#555', border: '0.5px solid #e0e0e0', cursor: 'pointer' }}>Override</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Coder note */}
            <p style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>Note for biller (optional)</p>
            <textarea value={coderNote} onChange={e => setCoderNote(e.target.value)}
              placeholder="Add a note for the billing team..."
              style={{ width: '100%', minHeight: 48, border: '0.5px solid #e0e0e0', borderRadius: 7, padding: 8, fontSize: 12, resize: 'vertical', background: '#fff' }}
              data-testid="coder-note-input" />
          </div>

          {/* Coding footer */}
          <div className="flex items-center justify-between" style={{ padding: '12px 0' }}>
            <button style={{ fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer' }}>
              &larr; Previous chart
            </button>
            <button onClick={handleMarkCoded}
              style={{ height: 36, padding: '0 20px', background: '#111', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              data-testid="mark-coded-btn">
              Mark as coded &mdash; send to billing &rarr;
            </button>
          </div>
        </div>
      </div>

      <Toaster position="bottom-right" />
    </div>
  );
};

const CoderViewWrapped = () => (
  <EnterpriseProvider>
    <CoderView />
  </EnterpriseProvider>
);

export default CoderViewWrapped;
